import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { MunicipalitiesService } from '@api/services/municipalities.service';
import { ButtonComponent } from '@ui/button/button.component';
import { TableColumn, TableComponent } from '@ui/table/table.component';
import { CardComponent } from '@ui/card/card.component';
import { ModalService } from '@ui/modal/modal.service';
import { MunicipalitiesModalComponent } from './municipalities-modal/municipalities-modal.component';
import { ConfirmService } from '@ui/confirm-modal/confirm.service';
import { PageHeaderService } from '@ui/app-layout/page-header.service';
import { finalize, catchError, EMPTY } from 'rxjs';
import { Municipality } from '@api/models/municipality.model';
import { IndexResponse } from '@api/models/response.model';

@Component({
  selector: 'app-municipalities',
  standalone: true,
  imports: [TableComponent, ButtonComponent, CardComponent],
  template: `
    <div class="h-[calc(100vh-theme(spacing.24))] flex flex-col gap-4">
      <ui-card>
        <div class="flex justify-between items-center">
          <h1 class="text-lg text-slate-900">Gemeenten</h1>
          <ui-button [icon]="'add'" (onClick)="addMunicipality()"
            >Nieuwe gemeente</ui-button
          >
        </div>
      </ui-card>

      <ui-table
        [columns]="columns"
        [data]="data()"
        [loading]="loading()"
        (onRowClick)="onRowClick($event)"
        [bordered]="false"
        [showPagination]="true"
        [meta]="meta()"
      ></ui-table>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MunicipalitiesComponent implements OnInit {
  municipalitiesService = inject(MunicipalitiesService);
  modalService = inject(ModalService);
  confirmService = inject(ConfirmService);
  pageHeader = inject(PageHeaderService);
  private cdr = inject(ChangeDetectorRef);

  columns: TableColumn[] = [
    { key: 'code', label: 'Code' },
    { key: 'name', label: 'Naam' },
    { key: 'customer_number', label: 'AFAS Klantnummer' },
    {
      key: 'csv_invoicing',
      label: 'CSV facturering',
      format: (value) => value ? 'Ja' : 'Nee',
    },
    { key: 'cbs_code', label: 'CBS code' },
    { key: 'tender.name', label: 'Aanbesteding' },
    {
      key: 'created_at',
      label: 'Aangemaakt op',
      align: 'right',
      format: (value) => new Date(value).toLocaleDateString(),
    },
    {
      key: 'actions',
      label: '',
      width: '200px',
      actions: [
        {
          label: 'Bewerken',
          icon: 'edit',
          variant: 'primary',
          onClick: (row) => this.editMunicipality(row),
        },
        {
          label: 'Verwijderen',
          icon: 'delete',
          variant: 'danger',
          onClick: (row) => this.deleteMunicipality(row),
        },
      ],
    },
  ];

  data = signal<Municipality[]>([]);
  meta = signal<IndexResponse<Municipality>['meta'] | null>(null);
  loading = signal<boolean>(false);

  ngOnInit() {
    this.getMunicipalities();
    this.pageHeader.setTitle('Gemeenten');
  }

  getMunicipalities() {
    this.loading.set(true);
    this.municipalitiesService
      .getMunicipalities()
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
        catchError((error) => {
          console.error('Error fetching municipalities:', error);
          return EMPTY;
        })
      )
      .subscribe((municipalities) => {
        this.data.set(municipalities.data);
        this.meta.set(municipalities.meta);
      });
  }

  addMunicipality() {
    let modalRef = this.modalService.open(MunicipalitiesModalComponent);

    modalRef.onClose.subscribe(() => {
      this.getMunicipalities();
    });
  }

  onRowClick(row: any) {
    this.editMunicipality(row);
  }

  editMunicipality(row: any) {
    let modalRef = this.modalService.open(MunicipalitiesModalComponent, {
      data: {
        municipality: row,
      },
    });

    modalRef.onClose.subscribe(() => {
      this.getMunicipalities();
    });
  }

  deleteMunicipality(row: any) {
    let confirm = this.confirmService.confirm({
      title: 'Verwijderen',
      message: 'Weet u het zeker?',
      confirmText: 'Verwijderen',
      cancelText: 'Annuleren',
    });

    confirm.then((confirmed) => {
      if (confirmed) {
        this.municipalitiesService.deleteMunicipality(row.id).subscribe(() => {
          this.getMunicipalities();
        });
      }
    });
  }
}
